import PropTypes from "prop-types";
import { useContext, useMemo, useState } from "react";
import { usePostRuleLevel } from "hooks/rule-levels/usePostRuleLevel";
import { useGetMachinesBySiteId } from "hooks/machines/useGetMachinesBySiteId";
import { SeverityTypeContext } from "contexts/SeverityTypeContext";
import { RuleLevelDialog } from "components/rules-management/RuleLevelDialog/RuleLevelDialog";
import { useErrorMessage } from "components/rules-management/RuleLevelDialog/useErrorMessage";
import { useSitesQuery } from "hooks/queries/useSitesQuery";

export const CreateRuleLevelDialog = ({ ruleId, open, onClose, onSuccess }) => {
  const [siteId, setSiteId] = useState();
  const {
    errorMessage,
    errorAcknowledged,
    onError,
    onErrorAcknowledged
  } = useErrorMessage();

  const {
    loading: loadingSeverityTypes,
    ruleSeverityTypes: severityTypes
  } = useContext(SeverityTypeContext);

  const { isLoading: loadingSites, data: _sites } = useSitesQuery();
  // ensure variable is defined for typescript
  const sites = useMemo(() => _sites?.results ?? [], [_sites]);

  const { loading: loadingMachines, machines } = useGetMachinesBySiteId(siteId);
  const { loading, error, postRuleLevelCallback } = usePostRuleLevel();

  const handleSuccess = () => {
    onErrorAcknowledged();
    onSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const handleClose = () => {
    onErrorAcknowledged();
    onClose();
  };

  const getNonFieldError = errorJson => {
    const validationError = errorJson?.non_field_errors;
    if (validationError) {
      return validationError.length > 0 ? validationError[0] : undefined;
    }
  };

  const handleSubmit = (
    { levelType, level, site, machine, recommendation },
    resetForm
  ) => {
    const tempRuleLevel = {
      rule: ruleId,
      level_type: levelType.id,
      level,
      site: site?.id,
      machine: machine?.id,
      recommendation
    };

    postRuleLevelCallback(tempRuleLevel).then(
      _success => {
        resetForm();
        handleSuccess();
      },
      errorResponse => {
        errorResponse.json().then(errorJson => {
          onError(getNonFieldError(errorJson));
        });
      }
    );
  };

  return (
    <RuleLevelDialog
      open={open}
      title="Create new threshold"
      loading={loading}
      error={error && !errorAcknowledged}
      errorMessage={errorMessage}
      sites={sites}
      loadingSites={loadingSites}
      machines={machines}
      loadingMachines={loadingMachines}
      severityTypes={severityTypes}
      loadingSeverityTypes={loadingSeverityTypes}
      confirmButtonLabel="Create threshold"
      handleSelectSiteId={setSiteId}
      onSubmit={handleSubmit}
      onClose={handleClose}
      initialValues={{
        levelType: null,
        level: 0,
        site: null,
        machine: null,
        recommendation: ""
      }}
    />
  );
};

CreateRuleLevelDialog.propTypes = {
  ruleId: PropTypes.number,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
};
